<template>
  <div>
    <h2 class="mt-6 mb-4 text-2xl font-bold">Заявки</h2>
    <div class="mb-6 flex items-center justify-between">
      <search-filter
        v-model="form.search"
        class="mr-4 w-full max-w-md"
        @reset="reset"
      >
        <label class="block text-gray-800" for="trashed-filter">
          Удаленные:
        </label>
        <select
          id="trashed-filter"
          v-model="form.trashed"
          class="form-select mt-1 w-full"
        >
          <option :value="null" />
          <option value="with">С Удаленными</option>
          <option value="only">Только Удаленные</option>
        </select>

        <label class="block text-gray-800 mt-6" for="expired-filter">
          Просроченные:
        </label>
        <select
          id="expired-filter"
          v-model="form.expired"
          class="form-select mt-1 w-full"
        >
          <option :value="null" />
          <option value="without">Без Просроченных</option>
          <option value="only">Только Просроченные</option>
        </select>
      </search-filter>

      <button class="btn-indigo" @click="modalNew = true">
        Создать <span class="hidden md:inline">Заявку</span>
      </button>
      <modal :open="modalNew" title="Создать Заявку" @close="modalNew = false">
        <new-order
          :workers="workers"
          :categories="categories"
          :clients="clients"
          :statuses="statuses"
          :order="newOrder"
          @success="modalNew = false"
        />
      </modal>
    </div>
    <div class="overflow-x-auto rounded bg-white shadow">
      <table class="w-full whitespace-nowrap">
        <thead>
          <tr class="text-left font-bold">
            <th class="px-6 pb-4 pt-6">№</th>
            <th class="px-6 pb-4 pt-6">Заявка</th>
            <th class="px-6 pb-4 pt-6">Адрес</th>
            <th class="px-6 pb-4 pt-6">Категория</th>
            <th class="px-6 pb-4 pt-6">Создано</th>
            <th class="px-6 pb-4 pt-6">Сделать до</th>
            <th class="px-6 pb-4 pt-6">Статус</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="order in orders.data"
            :key="order.id"
            class="focus-within:bg-gray-100 hover:bg-gray-100"
          >
            <td class="border-t max-w-24 truncate">
              <Link
                class="flex items-center px-6 py-4 focus:text-indigo-500"
                :href="$routes.edit_order(order.id)"
                aria-label="Edit"
              >
                № {{ order.id }}
                <icon
                  v-if="order.deleted_at"
                  name="trash"
                  class="ml-2 h-3 w-3 flex-shrink-0 fill-gray-500"
                />
              </Link>
            </td>
            <td class="border-t">
              <Link
                class="flex items-center px-6 py-4"
                :href="$routes.edit_order(order.id)"
                tabindex="-1"
                aria-label="Edit"
              >
                {{ order.title }}
              </Link>
            </td>

            <td class="border-t">
              <Link
                class="flex items-center px-6 py-4"
                :href="$routes.edit_order(order.id)"
                tabindex="-1"
                aria-label="Edit"
              >
                {{ order.client.address }}
              </Link>
            </td>
            <td class="border-t">
              <Link
                class="flex items-center px-6 py-4 text-wrap"
                :href="$routes.edit_order(order.id)"
                tabindex="-1"
                aria-label="Edit"
              >
                {{ order.category.title }}
              </Link>
            </td>
            <td class="border-t">
              <Link
                class="flex items-center px-6 py-4"
                :href="$routes.edit_order(order.id)"
                tabindex="-1"
                aria-label="Edit"
              >
                {{ $filters.formatDate(order.created_at) }}
              </Link>
            </td>
            <td class="border-t">
              <Link
                class="flex items-center px-6 py-4"
                :class="{ 'text-red-600': order['stale?'] === true }"
                :href="$routes.edit_order(order.id)"
                tabindex="-1"
                aria-label="Edit"
              >
                {{ $filters.formatDate(order.finish_at) }}
              </Link>
            </td>
            <td class="border-t">
              <Link
                class="flex items-center px-6 py-4"
                :href="$routes.edit_order(order.id)"
                tabindex="-1"
                aria-label="Edit"
              >
                {{ $filters.status(order.status) }}
              </Link>
            </td>
            <td class="w-px border-t">
              <Link
                class="flex items-center px-4"
                :href="$routes.edit_order(order.id)"
                tabindex="-1"
                aria-label="Edit"
              >
                <icon
                  name="cheveron-right"
                  class="block h-6 w-6 fill-gray-500"
                />
              </Link>
            </td>
          </tr>
          <tr v-if="orders.data.length === 0">
            <td class="border-t px-6 py-4" colspan="7">Нет заявок.</td>
          </tr>
        </tbody>
      </table>
    </div>
    <pagination :meta="orders.meta" />
  </div>
</template>

<script>
import { Link } from '@inertiajs/vue3';
import Icon from '@/Shared/Icon.vue';
import Layout from '@/Layouts/Main.vue';
import mapValues from 'lodash/mapValues';
import Pagination from '@/Shared/Pagination.vue';
import pickBy from 'lodash/pickBy';
import SearchFilter from '@/Shared/SearchFilter.vue';
import throttle from 'lodash/throttle';
import NewOrder from '@/Pages/Orders/_New.vue';
import Modal from '@/Shared/Modal.vue';

export default {
  components: {
    Link,
    Icon,
    Pagination,
    SearchFilter,
    Modal,
    NewOrder,
  },
  layout: Layout,
  props: {
    clients: {
      type: Array,
      required: true,
    },
    client: {
      type: Object,
      required: true,
    },
    categories: {
      type: Array,
      required: true,
    },
    statuses: {
      type: Array,
      required: true,
    },
    newOrder: {
      type: Object,
      required: true,
    },
    workers: {
      type: Array,
      required: true,
    },
    orders: {
      type: Object,
      required: true,
    },
    filters: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      form: {
        search: this.filters.search,
        trashed: this.filters.trashed,
        expired: this.filters.expired,
      },
      modalNew: false,
    };
  },
  watch: {
    form: {
      handler: throttle(function () {
        let query = pickBy(this.form);
        this.$inertia.get(
          this.$routes.edit_client(
            this.client.id,
            Object.keys(query).length ? query : { remember: 'forget' },
          ),
          {},
          {
            preserveState: true,
            preserveScroll: true,
            replace: true,
            only: ['orders'],
          },
        );
      }, 150),
      deep: true,
    },
  },
  methods: {
    reset() {
      this.form = mapValues(this.form, () => null);
    },
  },
};
</script>
